import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import './ManageEnrollments.css';

const ManageEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]); // Needed for the Add Modal
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Toolbar State (Search, Filter, Sort) ---
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('date-desc');

  // --- Modal States ---
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [editFormData, setEditFormData] = useState({ status: '', subscriptionTier: '', expiresAt: '' });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({ userEmail: '', courseId: '', subscriptionTier: 'Free', expiresAt: '' });
  const [isAdding, setIsAdding] = useState(false);

  // --- Fetch Data ---
  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch enrollments and courses concurrently
      const [enrollRes, coursesRes] = await Promise.all([
        apiService.getAllEnrollmentsAdmin(),
        apiService.getAllCoursesAdmin()
      ]);
      
      if (enrollRes.success) setEnrollments(enrollRes.data);
      if (coursesRes.success) setCourses(coursesRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Derived State: Filter & Sort ---
  // useMemo ensures this only recalculates when dependencies change, improving performance
  const processedEnrollments = useMemo(() => {
    let result = enrollments.filter(e => {
      // 1. Search
      const searchLower = searchTerm.toLowerCase();
      const matchName = e.user?.fullName?.toLowerCase().includes(searchLower) || false;
      const matchEmail = e.user?.email?.toLowerCase().includes(searchLower) || false;
      const matchCourse = e.course?.title?.toLowerCase().includes(searchLower) || false;
      const matchesSearch = matchName || matchEmail || matchCourse;

      // 2. Filter
      const matchesTier = filterTier === 'All' || e.subscriptionTier === filterTier;
      const matchesStatus = filterStatus === 'All' || e.status === filterStatus;

      return matchesSearch && matchesTier && matchesStatus;
    });

    // 3. Sort
    result.sort((a, b) => {
      if (sortBy === 'date-desc') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === 'date-asc') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'name-asc') return (a.user?.fullName || '').localeCompare(b.user?.fullName || '');
      if (sortBy === 'name-desc') return (b.user?.fullName || '').localeCompare(a.user?.fullName || '');
      return 0;
    });

    return result;
  }, [enrollments, searchTerm, filterTier, filterStatus, sortBy]);


  // --- Add Enrollment Handlers ---
  const handleOpenAddModal = () => {
    setAddFormData({ userEmail: '', courseId: '', subscriptionTier: 'Free', expiresAt: '' });
    setIsAddModalOpen(true);
  };

  const handleAddFormChange = (e) => setAddFormData({ ...addFormData, [e.target.name]: e.target.value });

  const handleSaveNewEnrollment = async (e) => {
    e.preventDefault();
    setIsAdding(true);
    try {
      const response = await apiService.adminEnrollUser(addFormData);
      if (response.success) {
        toast.success('Student enrolled successfully');
        fetchData(); // Refresh list
        setIsAddModalOpen(false);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to enroll student');
    } finally {
      setIsAdding(false);
    }
  };


  // --- Edit/Delete Handlers ---
  const handleOpenEdit = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setEditFormData({
      status: enrollment.status,
      subscriptionTier: enrollment.subscriptionTier,
      expiresAt: enrollment.expiresAt ? new Date(enrollment.expiresAt).toISOString().split('T')[0] : ''
    });
    setIsEditModalOpen(true);
  };

  const handleEditFormChange = (e) => setEditFormData({ ...editFormData, [e.target.name]: e.target.value });

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.updateEnrollmentAdmin(selectedEnrollment._id, editFormData);
      if (response.success) {
        toast.success('Enrollment updated successfully');
        fetchData();
        setIsEditModalOpen(false);
      } else throw new Error(response.message);
    } catch (err) { toast.error(err.message || 'Failed to update enrollment'); }
  };

  const handleDelete = async (enrollmentId) => {
    if (window.confirm('Remove this user from the course? This cannot be undone.')) {
      try {
        const response = await apiService.deleteEnrollmentAdmin(enrollmentId);
        if (response.success) {
          toast.success('Enrollment deleted');
          fetchData();
        } else throw new Error(response.message);
      } catch (err) { toast.error(err.message || 'Failed to delete enrollment'); }
    }
  };

  if (isLoading) return <div className="loading-spinner">Loading enrollments...</div>;
  if (error) return <div className="error-message">Error: {error}</div>;

  return (
    <div className="manage-enrollments-container">
      <div className="header-section">
        <h2>Manage Course Enrollments</h2>
        <button className="btn-primary-admin" onClick={handleOpenAddModal}>
          <i className="fas fa-plus"></i> Enroll Student
        </button>
      </div>

      {/* --- TOOLBAR: SEARCH, FILTER, SORT --- */}
      <div className="admin-toolbar">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input 
            type="text" 
            placeholder="Search by name, email, or course..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filters-group">
          <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)}>
            <option value="All">All Tiers</option>
            <option value="Free">Free</option>
            <option value="Plus">Plus</option>
            <option value="Premium">Premium</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="All">All Statuses</option>
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name-asc">Student Name (A-Z)</option>
            <option value="name-desc">Student Name (Z-A)</option>
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="admin-data-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Course</th>
              <th>Tier</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Expires At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {processedEnrollments.length > 0 ? (
              processedEnrollments.map((enrollment) => (
                <tr key={enrollment._id}>
                  <td>
                    <strong>{enrollment.user?.fullName || 'Deleted User'}</strong><br />
                    <small className="text-muted">{enrollment.user?.email || 'N/A'}</small>
                  </td>
                  <td>{enrollment.course?.title || 'Deleted Course'}</td>
                  <td><span className={`tier-badge tier-${enrollment.subscriptionTier?.toLowerCase()}`}>{enrollment.subscriptionTier}</span></td>
                  <td>{enrollment.progress.length} Lessons</td>
                  <td><span className={`status-badge status-${enrollment.status.toLowerCase().replace(' ', '-')}`}>{enrollment.status}</span></td>
                  <td>{enrollment.expiresAt ? new Date(enrollment.expiresAt).toLocaleDateString() : <span className="text-muted">Lifetime</span>}</td>
                  <td className="action-cell">
                    <button className="action-btn" onClick={() => handleOpenEdit(enrollment)} title="Edit Access"><i className="fas fa-edit"></i></button>
                    <button className="action-btn btn-delete" onClick={() => handleDelete(enrollment._id)} title="Remove Enrollment"><i className="fas fa-trash"></i></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" className="no-results">No enrollments match your criteria.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- ADD ENROLLMENT MODAL --- */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enroll Student in Course</h3>
              <button className="close-btn" onClick={() => setIsAddModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveNewEnrollment}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="userEmail">Student Email Address</label>
                  <input type="email" id="userEmail" name="userEmail" required placeholder="user@example.com" value={addFormData.userEmail} onChange={handleAddFormChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="courseId">Select Course</label>
                  <select id="courseId" name="courseId" required value={addFormData.courseId} onChange={handleAddFormChange}>
                    <option value="" disabled>-- Select a Course --</option>
                    {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="addSubscriptionTier">Access Tier</label>
                    <select name="subscriptionTier" id="addSubscriptionTier" value={addFormData.subscriptionTier} onChange={handleAddFormChange}>
                      <option value="Free">Free</option>
                      <option value="Plus">Plus</option>
                      <option value="Premium">Premium</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="addExpiresAt">Expiry Date (Optional)</label>
                    <input type="date" id="addExpiresAt" name="expiresAt" value={addFormData.expiresAt} onChange={handleAddFormChange} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={isAdding}>{isAdding ? 'Enrolling...' : 'Enroll Student'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- EDIT ENROLLMENT MODAL (Existing) --- */}
      {isEditModalOpen && (
        <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          {/* ... exactly the same edit modal structure from previous step ... */}
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Enrollment</h3>
              <button className="close-btn" onClick={() => setIsEditModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                <p className="text-muted mb-4">Student: <strong>{selectedEnrollment?.user?.fullName}</strong> <br/> Course: <strong>{selectedEnrollment?.course?.title}</strong></p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Access Tier</label>
                    <select name="subscriptionTier" value={editFormData.subscriptionTier} onChange={handleEditFormChange}>
                      <option value="Free">Free</option><option value="Plus">Plus</option><option value="Premium">Premium</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Course Status</label>
                    <select name="status" value={editFormData.status} onChange={handleEditFormChange}>
                      <option value="Not Started">Not Started</option><option value="In Progress">In Progress</option><option value="Completed">Completed</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Expiry Date (Leave blank for Lifetime)</label>
                  <input type="date" name="expiresAt" value={editFormData.expiresAt} onChange={handleEditFormChange} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEnrollments;